import { EventEmitter } from 'events';
import { WebSocket, MessageEvent } from 'isomorphic-ws';
import * as uuid from 'uuid';
import { BaseService } from './BaseService';


export namespace SansConnectService {
  export enum Actions {
    MESSAGE = 'SansConnectService/MESSAGE',
    READY = 'SansConnectService/READY',
    CLOSE = 'SansConnectService/CLOSE',
  };

  export interface RequestOptions {
    readonly timeoutMs?: number;
  }

  interface KeyValuePair { [key: string]: unknown };
  export interface Request extends KeyValuePair {
    readonly action: string;
  }

  export interface Message {
    readonly action: string;
    readonly data: any; // TODO
  }

  export interface ActionResponse {
    readonly requestId: string;
    readonly error?: any;
    readonly response?: any;
    /* why can't i do this with jsii */
    /*
    readonly response?: {
      statusCode: number;
    };
    */
  }


  export class Api extends BaseService {

    private _socket?: WebSocket;
    private _emitter: EventEmitter;

    private _handlers: {
      open: () => void;
      ping: () => void;
      pong: () => void;
      close: () => void;
      message: (event: MessageEvent) => void;
    };

    constructor() {
      super({ configPath: 'services.connect' });
      this._emitter = new EventEmitter();

      this._handlers = {
        pong: () => {
          console.log('pong');
        },
        ping: () => {
          console.log('received ping');
        },
        open: () => {
          this._emitter.emit('ready');
        },
        close: () => {
          this._emitter.emit('close');
        },
        message: async (event: MessageEvent) => {
          const data = event.data;
          console.log(data.toString());
          if (data?.toString()) {
            try {
              const message = JSON.parse(data.toString());
              if (message?.action === 'message') {
                this._emitter.emit('message', message);
              } else if (message.requestId) {
                this._emitter.emit('response', message);
              }
            } catch (ex) {
              console.log(ex);
            }
          } else {
            console.log(data);
          }
        },
      };
      this._emitter.on('message', (message) => {
        this.api.systemEvents.dispatch({
          action: Actions.MESSAGE,
          payload: message,
        });
      });
      this._emitter.on('ready', () => {
        this.api.systemEvents.dispatch({
          action: Actions.READY,
        });
      });
      this._emitter.on('close', () => {
        this.api.systemEvents.dispatch({
          action: Actions.CLOSE,
        });
      });
    }

    async connectUrl() {
      const config = await this.config();
      return config?.wsEndpoint;
    }

    public async disconnect() {
      this._socket?.close();
      delete this._socket;
      return true;
    }

    isConnected() {
      // TODO make this work properly
      return this._socket !== undefined;
    }

    async connect(): Promise<boolean> {
      const tokens = await this.api.tokens.freshTokens();
      const url = await this.connectUrl();
      const ws = new WebSocket(`${url}?token=${tokens.id}`);

      ws.onclose = this._handlers.close;
      ws.onopen = this._handlers.open;
      ws.onmessage = this._handlers.message;
      ws.onerror = console.log;

      /*
      for (const [key, handler] of Object.entries(this._handlers)) {
        ws.on(key, handler);
      }
      */
      this._socket = ws;

      return true;
    }

    async executeRequest<ResponseType extends ActionResponse>(request: Request, options?: RequestOptions):Promise<ResponseType> {
      const requestId = uuid.v4();

      const promise = new Promise<ResponseType>((resolve, reject) => {
        if (!this._socket) {
          return reject({ error: 'socket not found' });
        }
        const responseReceived = (response: ResponseType) => {
          if (response.requestId === requestId) {
            if (response.error || response.response?.statusCode && response.response.statusCode >= 400) {
              reject(response);
            } else {
              resolve(response as ResponseType);
            }
            this._emitter.off('response', responseReceived);
          }
        };

        this._emitter.on('response', responseReceived);
        const r = {
          requestId,
          ...request,
        };
        this._socket.send(JSON.stringify(r));
      });

      if (options?.timeoutMs) {
        return Promise.race([
          promise,
          new Promise<ResponseType>((_resolve, reject) => {
            setTimeout(() => reject('TimeoutError'), options.timeoutMs);
          }),
        ]);

      } else {
        return promise;
      }
    }

    subscribe(channelId: string) {
      return this.executeRequest({
        action: '/channel/subscribe',
        data: {
          channelId,
        },
      });
    }

    send(channelId: string, data: any) {
      return this.executeRequest({
        action: '/channel/send',
        data: {
          channelId,
          ...data,
        },
      });
    }
  }
}

export default SansConnectService.Api;
